import { DateRangePicker } from 'daterangepicker';
import React from 'react';
import { formDateInputUtils } from '../utils';
import { FieldPath, SetValueConfig } from 'react-hook-form';
import { Nullable } from '@/Types';
import { InputMask } from 'imask';
import { formUtils } from '@/Utils/FormUtils';

type Props<TData> = {
  name: FieldPath<TData>;
  setValue?: (name: any, value: any, options?: SetValueConfig) => void;
  maskRef: React.MutableRefObject<Nullable<InputMask<Record<string, unknown>>>>;
  format: string;
  maxDate?: string | Date;
  isRange: boolean;
  onChange?: (val: string) => void;
};

export function useDateRangeEventListeners<TData extends object>({
  name,
  setValue,
  maskRef,
  format,
  maxDate,
  isRange,
  onChange,
}: Props<TData>) {
  const onSetValue = (value: string) => {
    setValue?.(name, value, { shouldValidate: true, shouldDirty: true });
    maskRef.current?.updateValue();
  };

  const onApply = (picker: DateRangePicker, setDisplayValue: (value: string) => void) => {
    const value = formDateInputUtils.formatPicker(picker, format, isRange);

    onSetValue(value);
    onChange?.(value);

    if (picker.chosenLabel) {
      setDisplayValue(picker.chosenLabel);
    } else {
      setDisplayValue(value);
    }
  };

  const onCancel = (setDisplayValue: (value: string) => void) => {
    setDisplayValue('');
    onSetValue('');
  };

  const onSetCurrentDate = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      const newValue = formDateInputUtils.fillMaskEmptyValues(event.currentTarget.value, format, maxDate);
      onSetValue(newValue);

      // Switch to next element
      formUtils.switchToNextNode(event, 2);
    }
  };

  return {
    onSetCurrentDate,
    onSetValue,
    onApply,
    onCancel,
  };
} // useDateRangeEventListeners
